import { z } from 'zod';
import { MAX_VARIANT_COUNT } from '../common/job-api.schema';

/** Mirrors the WalletTransactionType enum in packages/db/prisma/schema.prisma. */
export const WalletTransactionTypeSchema = z.enum([
  'JOB_DEDUCTION',
  'TOP_UP',
  'REFUND',
  'SUBSCRIPTION_CREDIT',
  'TRAINING_HOLD',
  'TRAINING_CONFIRMED',
  'TRAINING_REFUND',
  'ADMIN_DEBIT',
  'REFERRAL_CREDIT',
]);

export const WalletSchema = z.object({
  id: z.string(),
  userId: z.string(),
  balance: z.string(), // Decimal serialized as string
  updatedAt: z.date(),
});

export const WalletTransactionSchema = z.object({
  id: z.string(),
  walletId: z.string(),
  type: WalletTransactionTypeSchema,
  amount: z.string(), // Decimal serialized as string
  balanceAfter: z.string(), // Decimal serialized as string
  jobId: z.string().nullable().optional(),
  stripePaymentIntentId: z.string().nullable().optional(),
  createdAt: z.date(),
});

export const ApiPricingSchema = z.object({
  id: z.string(),
  provider: z.string(),
  model: z.string(),
  jobType: z.enum([
    'TEXT_TO_IMAGE',
    'IMAGE_TO_IMAGE',
    'IMAGE_UPSCALE',
    'VIDEO_GENERATION',
    'AUDIO_GENERATION',
    'TEXT_GENERATION',
    'IMAGE_ANALYSIS',
    'CUSTOM_TOOL',
  ]),
  priceUsd: z.string(), // Decimal serialized as string
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// API Request/Response schemas

export const GetBalanceResponseSchema = z.object({
  balance: z.string(),
});

export const TopUpRequestSchema = z.object({
  amount: z.number().positive().min(1).max(1000).multipleOf(0.01),
});

export const TopUpResponseSchema = z.object({
  clientSecret: z.string(),
  paymentIntentId: z.string(),
});

/**
 * Settings that change what a run costs, flattened into the estimate query string.
 *
 * Model prices are quoted per second, per megapixel or per 1000 characters, so a quote that
 * ignores the request would be the price of a single unit. These are the same parameter names
 * the canvas sends in a job's `settings` (and the aliases the billing unit maths reads), kept
 * optional so a caller that omits them still gets a quote computed from the billing defaults.
 */
const estimateSeconds = z.coerce.number().positive().max(3600).optional();
const estimateFrameCount = z.coerce.number().int().positive().max(100_000).optional();
const estimateFrameRate = z.coerce.number().positive().max(240).optional();
const estimatePixels = z.coerce.number().int().positive().max(16_384).optional();
const estimateTokenBudget = z.coerce.number().int().positive().max(10_000_000).optional();

export const JobEstimatePricingParamsSchema = z.object({
  variantCount: z.coerce.number().int().min(1).max(MAX_VARIANT_COUNT).optional(),
  duration: estimateSeconds,
  duration_seconds: estimateSeconds,
  audio_duration_seconds: estimateSeconds,
  seconds_total: estimateSeconds,
  num_frames: estimateFrameCount,
  number_of_frames: estimateFrameCount,
  frames: estimateFrameCount,
  fps: estimateFrameRate,
  frames_per_second: estimateFrameRate,
  frame_rate: estimateFrameRate,
  // Segment-priced video (pixverse, longcat) is billed per 5-second block, so a quote that
  // ignored the segment count would show the price of one block for a ten-block run.
  num_segments: z.coerce.number().int().positive().max(100).optional(),
  width: estimatePixels,
  height: estimatePixels,
  image_size: z.string().max(64).optional(),
  video_size: z.string().max(64).optional(),
  resolution: z.string().max(64).optional(),
  aspect_ratio: z.string().max(32).optional(),
  max_tokens: estimateTokenBudget,
  max_new_tokens: estimateTokenBudget,
});

/**
 * GET /api/jobs/estimate query: the model to quote plus the optional pricing parameters above.
 * Unknown query keys are stripped.
 */
export const JobEstimateQuerySchema = z
  .object({
    provider: z.string(),
    model: z.string(),
  })
  .extend(JobEstimatePricingParamsSchema.shape);

export const JobEstimateResponseSchema = z.object({
  priceUsd: z.string(),
  provider: z.string(),
  model: z.string(),
});

export type WalletTransactionType = z.infer<typeof WalletTransactionTypeSchema>;
export type Wallet = z.infer<typeof WalletSchema>;
export type WalletTransaction = z.infer<typeof WalletTransactionSchema>;
export type ApiPricing = z.infer<typeof ApiPricingSchema>;
export type GetBalanceResponse = z.infer<typeof GetBalanceResponseSchema>;
export type TopUpRequest = z.infer<typeof TopUpRequestSchema>;
export type TopUpResponse = z.infer<typeof TopUpResponseSchema>;
export type JobEstimatePricingParams = z.infer<typeof JobEstimatePricingParamsSchema>;
export type JobEstimateQuery = z.infer<typeof JobEstimateQuerySchema>;
export type JobEstimateResponse = z.infer<typeof JobEstimateResponseSchema>;
